import { useEffect } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";

interface OnboardingOverlayProps {
  isOpen: boolean;
  onDismiss: () => void;
  documentColor: string;
}

export function OnboardingOverlay({ isOpen, onDismiss, documentColor }: OnboardingOverlayProps) {
  useEffect(() => {
    if (!isOpen) return;
    
    const timer = setTimeout(() => {
      onDismiss();
    }, 10000);
    
    return () => clearTimeout(timer);
  }, [isOpen, onDismiss]);

  useEffect(() => {
    if (!isOpen) return;
    
    const handleInteraction = () => {
      onDismiss();
    };
    
    document.addEventListener('click', handleInteraction, { once: true });
    
    return () => {
      document.removeEventListener('click', handleInteraction);
    };
  }, [isOpen, onDismiss]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div 
        className="absolute inset-0 bg-background/80 backdrop-blur-sm"
        onClick={onDismiss}
      />
      
      <div className="relative z-10 bg-popover border border-border/60 rounded-md shadow-2xl p-6 max-w-sm mx-4">
        <Button
          variant="ghost"
          size="sm"
          className="absolute top-2 right-2 h-7 w-7 p-0 text-muted-foreground hover:text-foreground rounded-sm"
          onClick={onDismiss}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
        
        <div className="mb-4">
          <div 
            className="w-10 h-10 rounded-md mb-3 flex items-center justify-center"
            style={{ backgroundColor: documentColor + '15' }}
          >
            <div 
              className="w-4 h-4 rounded-full"
              style={{ backgroundColor: documentColor }}
            />
          </div>
          <h3 className="text-sm font-semibold text-foreground tracking-tight">
            Welcome to Commentt.ai
          </h3>
        </div>
        
        <div className="mb-5">
          <p className="text-xs text-muted-foreground leading-relaxed">
            Click anywhere on the page to start commenting.
          </p>
        </div>
        
        <Button 
          onClick={onDismiss}
          className="w-full h-8 text-xs font-medium rounded-sm"
          style={{ backgroundColor: documentColor }}
        >
          Got it
        </Button>
      </div>
    </div>
  );
}
